import Papa from 'papaparse';
import type { BonusLevel } from '@/types';
import { BONUS_CSV_URL } from '@config/constants';

interface BonusCSVRow {
  points: string;
  prod1: string;
  prod2: string;
  prod3: string;
  produits_concaténés: string;
}

export class BonusParser {
  private static bonusLevels: BonusLevel[] = [];
  private static isLoaded = false;

  /**
   * Load bonus levels from CSV
   */
  static async loadBonusLevels(): Promise<void> {
    if (this.isLoaded) {
      console.log('📦 Bonus levels already loaded');
      return;
    }

    try {
      console.log('📥 Fetching bonus CSV from:', BONUS_CSV_URL);
      
      // Utiliser le cache si disponible
      const CacheService = (await import('./cache-service')).CacheService;
      const response = await CacheService.fetchWithCache(BONUS_CSV_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const csvText = await response.text();

      const result = Papa.parse<BonusCSVRow>(csvText, {
        header: true,
        skipEmptyLines: true,
      });

      this.bonusLevels = result.data
        .map(row => ({
          points: parseInt(row.points) || 0,
          products: row.produits_concaténés
            ? row.produits_concaténés.split(' | ').filter(p => p)
            : [],
        }))
        .filter(level => level.points > 0); // Only keep valid levels

      // Sort by points ascending
      this.bonusLevels.sort((a, b) => a.points - b.points);

      this.isLoaded = true;
      console.log('✅ Bonus levels loaded:', this.bonusLevels);
    } catch (error) {
      console.error('❌ Error loading bonus levels:', error);
      // Continue without bonus in case of error
      this.bonusLevels = [];
      this.isLoaded = true;
    }
  }

  /**
   * Get bonus products based on total points
   */
  static getBonusProducts(totalPoints: number): string[] {
    // Find the highest bonus level achieved
    let bonusProducts: string[] = [];

    for (const level of this.bonusLevels) {
      if (totalPoints >= level.points) {
        // Take the highest level achieved
        bonusProducts = level.products;
      } else {
        break; // Levels are sorted, we can stop
      }
    }

    console.log(`🎁 Bonus products for ${totalPoints} points:`, bonusProducts);
    return bonusProducts;
  }

  /**
   * Get all bonus levels (for display purposes)
   */
  static getAllBonusLevels(): BonusLevel[] {
    return [...this.bonusLevels];
  }

  /**
   * Get next bonus level for a given point total
   */
  static getNextBonusLevel(currentPoints: number): BonusLevel | null {
    for (const level of this.bonusLevels) {
      if (currentPoints < level.points) {
        return level;
      }
    }
    return null;
  }

  /**
   * Reset bonus levels (useful for testing)
   */
  static reset(): void {
    this.bonusLevels = [];
    this.isLoaded = false;
  }
}
